import { advancedResponseValidator, get } from "./api";
import { CurrentPlanStatus, ResourceType, SizeRangeEnum, UserType } from "../interfaces/general";
import API from "../urls/api";
import { setAuthStatus, setUser } from "../redux/slices/authSlice";
import store from "../redux/store";
import imageFileIcon from "../assets/images/fileManager/ImageFileIcon.svg";
import otherFileIcon from "../assets/images/fileManager/OtherFileIcon.svg";
import videoFileIcon from "../assets/images/fileManager/VideoFileIcon.svg";
import musicFileIcon from "../assets/images/fileManager/MusicFileIcon.svg";
import folderIcon from "../assets/images/fileManager/Folder.svg";
import { TimeType } from "../utils/component/timePicker/timePicker.interface";
import PATH from "../urls/path";
import { createBrowserHistory } from "history";
import { toast } from "mobin-toast";
import { CalendarOptions } from "../pages/layout/header/search/search.interface";
import moment from "moment";

export const FindMaximum = (list: any) => {
    let max: any = undefined;
    if (list.length !== 0) {
        return Math.max.apply(null, list);
    }
    return max;
};

export const isTouchScreen = () =>
    "ontouchstart" in window || ((window as any)["DocumentTouch"] && document instanceof (window as any)["DocumentTouch"]);

export function getUserProfile() {
    const dispatch = store.dispatch;
    const history = createBrowserHistory();
    return new Promise((resolve) => {
        get((data: UserType, status) => {
            if (advancedResponseValidator(data, status)) {
                dispatch(setUser({ ...data, phone: addZeroToStart(data.phone) }));
                dispatch(setAuthStatus("VALID"));
                resolve(true);
            } else {
                if (history.location.pathname === PATH.publicFolder) {
                    dispatch(setAuthStatus("INVALID"));
                }
            }
        }, API.auth.profile());
    });
}

export const getRemainingTime = (time: any, serverTime: number) => {
    return Math.floor(Math.floor((serverTime * 1000 - time * 1000) / 86400000));
};

export function CorrectPhoneNumber(phone: string) {
    return "0" + phone.replace(" ", "");
}

export function apiErrorFormat(errors: any) {
    const format: any = {};
    Object.entries(errors).forEach((err: any) => {
        format[err[0]] = err[1][0];
    });
    return format;
}

export function secondsToHms(seconds: number) {
    if (seconds) {
        seconds = Number(seconds);
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor((seconds % 3600) % 60);

        const hDisplay = h > 0 ? h + (m >= 1 ? " ساعت و " : " ساعت ") : "";
        const mDisplay = m > 0 ? m + (s >= 1 ? " دقیقه و " : " دقیقه ") : "";
        const sDisplay = s > 0 ? s + (s === 1 ? " ثانیه" : " ثانیه") : "";
        return hDisplay + mDisplay + sDisplay;
    } else {
        return "0 ثانیه";
    }
}

export function secondsToHmsReverse(seconds: number) {
    seconds = Number(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);

    const hDisplay = h > 0 ? (h === 1 ? " ساعت " : " ساعت ") + `${h} و` : "";
    const mDisplay = m > 0 ? (m === 1 ? " دقیقه " : " دقیقه ") + `${m} و` : "";
    const sDisplay = s > 0 ? (s === 1 ? " ثانیه " : " ثانیه ") + s : "";
    return hDisplay + mDisplay + sDisplay;
}

export function secondsToHmsNumber(seconds: number) {
    seconds = Number(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);

    return `${addZeroBefore(h)}:${addZeroBefore(m)}:${addZeroBefore(s)}`;
}

export const secondsToDhm = (seconds: number) => {
    let d = Math.floor(seconds / (3600 * 24));
    let h = Math.floor((seconds % (3600 * 24)) / 3600);
    let m = Math.floor((seconds % 3600) / 60);
    return [d, h, m];
};

export function stringToSecond(hms: string) {
    const a = hms.split(":");
    return +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
}

export const UnitConvertStandard = (max_number: number, lang: "fa" | "en" = "en") => {
    if (max_number / 1024 < 1) {
        return { value: max_number, unit: lang === "en" ? "B" : "بایت" };
    } else if (max_number / (1024 * 1024) < 1) {
        return { value: max_number / 1024, unit: lang === "en" ? "KB" : "کیلوبایت" };
    } else if (max_number / (1024 * 1024 * 1024) < 1) {
        return { value: max_number / (1024 * 1024), unit: lang === "en" ? "MB" : "مگابایت" };
    } else if (max_number / (1024 * 1024 * 1024 * 1024)) {
        return { value: max_number / (1024 * 1024 * 1024), unit: lang === "en" ? "GB" : "گیگابایت" };
    } else if (max_number / (1024 * 1024 * 1024 * 1024 * 1024)) {
        return { value: max_number / (1024 * 1024 * 1024 * 1024), unit: lang === "en" ? "TB" : "ترابایت" };
    } else return { value: 0, unit: "" };
};

export const getSizeByRangeType = (size: number, range: SizeRangeEnum) => {
    switch (range) {
        case SizeRangeEnum.B:
            return size;
        case SizeRangeEnum.KB:
            return size / 1024;
        case SizeRangeEnum.MB:
            return size / (1024 * 1024);
        case SizeRangeEnum.GB:
            return size / (1024 * 1024 * 1024);
        case SizeRangeEnum.TB:
            return size / (1024 * 1024 * 1024 * 1024);
        default:
            return size;
    }
};

export const getSizeRangeType = (size: number) => {
    if (size / 1024 < 1) {
        return SizeRangeEnum.B;
    } else if (size / (1024 * 1024) < 1) {
        return SizeRangeEnum.KB;
    } else if (size / (1024 * 1024 * 1024) < 1) {
        return SizeRangeEnum.MB;
    } else if (size / (1024 * 1024 * 1024 * 1024)) {
        return SizeRangeEnum.GB;
    } else if (size / (1024 * 1024 * 1024 * 1024 * 1024)) {
        return SizeRangeEnum.TB;
    } else return SizeRangeEnum.B;
};

export const TimeConvertStandard = (day: number, _lang: "fa" | "en" = "en") => {
    const y = Math.floor(day / 365);
    const m = Math.floor((day % 365) / 30);
    const d = Math.floor((day % 365) % 30);

    const yDisplay = y > 0 ? y + (m >= 1 ? " سال و " : " سال ") : "";
    const mDisplay = m > 0 ? m + (d >= 1 ? " ماه و " : " ماه ") : "";
    const dDisplay = d > 0 ? d + (d === 1 ? " روز" : " روز") : "";
    return yDisplay + mDisplay + dDisplay;
};

export function isFolder(resource: ResourceType) {
    return !resource.key;
}

export function isFolderWithKey(key: string | null) {
    return !key;
}

export function getResourceName(path: string) {
    const parts = path.split("/").filter((item) => item !== "");
    return parts[parts.length - 1];
}

export const getFileExtension = (filename: string) => filename.split(".").pop()?.toLocaleLowerCase();

export const getStatusPlan = (quotaObject: any, validTimeCurrent: any, serverTime: any) => {
    if ((quotaObject && quotaObject.used > quotaObject.total) || quotaObject.total === 0) return 3;
    else if (quotaObject && quotaObject.plan_exp_time && validTimeCurrent === 0 && getRemainingTime(quotaObject.plan_exp_time, serverTime)) return 2;
    else return 1;
};

export const imageFormatList = ["jpg", "jpeg", "jfif", "pjpeg", "pjp", "svg", "webp", "png", "gif", "ico", "ai", "bmp", "ps", "tif", "heic"].sort(
    (a, b) => a.length - b.length
);
const imageFormatRegex = new RegExp(`(${imageFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

export const videoFormatList = ["avi", "mp4", "MTS", "M2TS", "TS", "wmv", "qt", "ogg", "3gp", "mkv", "m4a", "rm", "wmv", "flv", "webm", "m4v"].sort(
    (a, b) => a.length - b.length
);
const videoFormatRegex = new RegExp(`(${videoFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

export const musicFormatList = ["ogg", "mp3", "flac", "wav"].sort((a, b) => a.length - b.length);
const musicFormatRegex = new RegExp(`(${musicFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const documentFormatList = [
    "rtf",
    "tar",
    "txt",
    "doc",
    "docx",
    "pdf",
    "md",
    "xml",
    "csv",
    "ppt",
    "pptx",
    "odt",
    "tex",
    "bin",
    "dmg",
    "iso",
    "toast",
    "log",
    "key",
    "odp",
    "pps",
    "m3u8",
    "xls",
    "xlsx",
    "pmd",
    "eps",
    "dcr"
].sort((a, b) => a.length - b.length);
const documentFormatRegex = new RegExp(`(${documentFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const compressFormatList = ["zip", "7z", "rar", "tar.gz", "arj", "deb", "pkg"].sort((a, b) => a.length - b.length);
const compressFormatRegex = new RegExp(`(${compressFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const executableFormatList = ["deb", "exe", "apk", "bat", "bin", "cgi", "com", "gadget", "jar", "msi ", "wsf"].sort((a, b) => a.length - b.length);
const executableFormatRegex = new RegExp(`(${executableFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const codeFormatList = [
    "json",
    "sql",
    "py",
    "js",
    "jsx",
    "ts",
    "tsx",
    "html",
    "css",
    "c",
    "cc",
    "h",
    "cpp",
    "hpp",
    "tsx",
    "java",
    "php",
    "dev",
    "swift",
    "sh",
    "yml",
    "yaml",
    "xml",
    "xaml"
].sort((a, b) => a.length - b.length);
const codeFormatRegex = new RegExp(`(${codeFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const systemFileFormatList = ["bak", "cab", "cfg", "cpl", "cur", "dll", "dmp", "drv", "icns", "ini", "lnk", "msi", "sys", "tmp"].sort(
    (a, b) => a.length - b.length
);
const systemFileFormatRegex = new RegExp(`(${systemFileFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const fontFormatList = ["fnt", "fon", "otf", "ttf"].sort((a, b) => a.length - b.length);
const fontFormatRegex = new RegExp(`(${fontFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const internetRelatedFileExtensionFormatList = ["cfm", "part", "jsp", "pl", "xhtml"].sort((a, b) => a.length - b.length);
const internetRelatedFileExtensionFormatRegex = new RegExp(`(${internetRelatedFileExtensionFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const emailFormatList = ["email", "eml", "emlx", "msg", "oft ", "ost", "pst", "vcf"].sort((a, b) => a.length - b.length);
const emailFormatRegex = new RegExp(`(${emailFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

const otherFormatList = ["srt", "ico", "ai", "bmp", "ps", "tif"].sort((a, b) => a.length - b.length);
const otherFormatRegex = new RegExp(`(${otherFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

export const previewValidFormats = [
    ...imageFormatList,
    !isTouchScreen() && "xlsx",
    "hls",
    "dash",
    "mp4",
    "webm",
    "mkv",
    "mov",
    "mp3",
    "ogg",
    "pdf",
    "m4v",
    "txt",
    "heic"
];
export const formatsHasPrint: any = [...imageFormatList, "pdf", "txt"];

const os = ["Windows", "Linux", "Mac"];

const allFormatList = [
    ...imageFormatList,
    ...videoFormatList,
    ...documentFormatList,
    ...compressFormatList,
    ...executableFormatList,
    ...codeFormatList,
    ...systemFileFormatList,
    ...fontFormatList,
    ...internetRelatedFileExtensionFormatList,
    ...emailFormatList,
    ...otherFormatList,
    ...musicFormatList
].sort((a, b) => a.length - b.length);
export const allFormatRegex = new RegExp(`(${allFormatList.map((item) => `\\.${item}`).join("|")})$`, "i");

export const setColorIconFile = (name: string) => {
    const formatList = name.match(allFormatRegex);
    if (formatList && formatList.length) {
        const format = formatList[0];
        if (imageFormatRegex.test(format)) {
            return "image-files-bg";
        } else if (videoFormatRegex.test(format)) {
            return "video-files-bg";
        } else if (musicFormatRegex.test(format)) {
            return "music-files-bg";
        } else {
            return "other-files-bg";
        }
    } else return "other-files-bg";
};

export const setIconFile = (name: string, isFolder?: boolean) => {
    const formatList = name.match(allFormatRegex);
    if (!!isFolder) return folderIcon;
    if (formatList && formatList.length) {
        const format = formatList[0];
        if (imageFormatRegex.test(format)) {
            return imageFileIcon;
        } else if (videoFormatRegex.test(format)) {
            return videoFileIcon;
        } else if (musicFormatRegex.test(format)) {
            return musicFileIcon;
        } else if (otherFormatRegex.test(format)) {
            return otherFileIcon;
        } else return otherFileIcon;
    } else return otherFileIcon;
};

export function getFileManagerParent(): any {
    const params = new URLSearchParams(window.location.search);
    return params.get("path") ? params.get("path") : undefined;
}

export function getFileManagerParentId(): any {
    const resource = store.getState().path.currentResource;
    if (resource) return resource.id;
}

export function getHashPathPublicLinkFolder() {
    const params = new URLSearchParams(window.location.search);
    return params.get("h") ? params.get("h") : undefined;
}

export function getRootPublicLinkFolder() {
    const params = new URLSearchParams(window.location.search);
    return params.get("root") ? params.get("root") : undefined;
}

export const getFileNameWithoutFormat = (resource: ResourceType) => {
    let info = { name: resource.name, format: "" };
    if (!isFolder(resource)) {
        if (resource.name.match(allFormatRegex)) {
            let postfix: any = resource.name.match(allFormatRegex)!;
            info = {
                name: resource.name.replace(postfix[0], ""),
                format: postfix[0]
            };
        }
    }
    return info;
};

export const getFileNameWithoutFormatWithName = (name: string) => {
    let info = { name: name, format: "" };
    allFormatList.forEach((format) => {
        if (name.toLowerCase().endsWith(format)) {
            info = {
                name: name.slice(0, name.toLowerCase().lastIndexOf(format) - 1),
                format: name.slice(name.toLowerCase().lastIndexOf(format) - 1)
            };
        }
    });
    return info;
};

export const convertByteToGb = (value: number) => {
    const GB = value / (1024 * 1024 * 1024);

    if (isNumberInt(GB)) return GB;
    else return GB.toFixed(2);
};

const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
const iosPlatforms = ["iPhone", "iPad", "iPod"];

export const getOS = () => {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    let os = null;

    if (macosPlatforms.indexOf(platform) !== -1) os = "mac";
    else if (iosPlatforms.indexOf(platform) !== -1) os = "ios";
    else if (windowsPlatforms.indexOf(platform) !== -1) os = "windows";
    else if (/Android/.test(userAgent)) os = "android";
    else if (/Linux/.test(platform)) os = "linux";

    return os;
};

export function s_to_mmss(time = 0) {
    const second = time % 60;
    const min = Math.floor(time / 60);
    return (min < 10 ? "0" + min : min) + ":" + (second < 10 ? "0" + second : second);
}

export async function getFilesFromDataTransferItems(dataTransferItems: any, options = { raw: false }) {
    const checkErr = (err: any) => {
        // @ts-ignore
        if (getFilesFromDataTransferItems.didShowInfo) return;
        if (err.name !== "EncodingError") return;
        // @ts-ignore
        getFilesFromDataTransferItems.didShowInfo = true;
        const infoMsg =
            `${err.name} occured within datatransfer-files-promise module\n` +
            `Error message: "${err.message}"\n` +
            "Try serving html over http if currently you are running it from the filesystem.";
        console.warn(infoMsg);
    };

    const readFile = (entry: any, path = "") => {
        return new Promise((resolve, reject) => {
            entry.file(
                (file: any) => {
                    if (!options.raw) file.filepath = path + file.name; // save full path
                    resolve(file);
                },
                (err: any) => {
                    checkErr(err);
                    reject(err);
                }
            );
        });
    };

    const dirReadEntries = (dirReader: any, path: any) => {
        return new Promise((resolve, reject) => {
            dirReader.readEntries(
                async (entries: any) => {
                    let files: any = [];
                    for (let entry of entries) {
                        const itemFiles = await getFilesFromEntry(entry, path);
                        files = files.concat(itemFiles);
                    }
                    resolve(files);
                },
                (err: any) => {
                    checkErr(err);
                    reject(err);
                }
            );
        });
    };

    const readDir = async (entry: any, path: any) => {
        const dirReader = entry.createReader();
        const newPath = path + entry.name + "/";
        let files: any = [];
        let newFiles: any;
        do {
            newFiles = await dirReadEntries(dirReader, newPath);
            files = files.concat(newFiles);
        } while (newFiles.length > 0);
        return files;
    };

    const getFilesFromEntry = async (entry: any, path = "") => {
        if (entry.isFile) {
            const file = await readFile(entry, path);
            return [file];
        }
        if (entry.isDirectory) {
            const files = await readDir(entry, path);
            return files;
        }
        // throw new Error('Entry not isFile and not isDirectory - unable to get files')
    };

    let files: any = [];
    let entries = [];

    // Pull out all entries before reading them
    for (let i = 0, ii = dataTransferItems.length; i < ii; i++) {
        entries.push(dataTransferItems[i].webkitGetAsEntry());
    }

    // Recursively read through all entries
    for (let entry of entries) {
        const newFiles = await getFilesFromEntry(entry);
        files = files.concat(newFiles);
    }

    return files;
}

export const jalCalendarHeader = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

export const jalMonthNames = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"];

export const isNegative = (num: number) => {
    if (Math.sign(num) === -1) return true;
    return false;
};

export const isInt = (num: number) => num % 1 === 0;

export const separateNameAndFormat = (str: string) => {
    const splitedStr = str.split(".");
    const format = splitedStr.pop();
    return {
        name: splitedStr.join(""),
        format
    };
};

export const forbiddenCharacters = ["+", "*", "?", "^", "$", "[", "]", "{", "}", "|", "/", "\\", "~", "#", "@", "!"];

export function typeChecker(data: any) {
    if (!isNaN(data)) {
        return Number(data);
    } else if (data === "true") {
        return true;
    } else if (data === "false") {
        return false;
    } else if (data === "null") {
        return null;
    } else if (data === "undefined") {
        return null;
    } else {
        return data;
    }
}

export const sliceIntoChunks = (arr: number[], chunkSize: number) => {
    const res = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        res.push(chunk);
    }
    return res;
};

export function searchUrlToObject() {
    const query = new URLSearchParams(window.location.search);
    const queryArray = query.get("q")?.split("|");
    let object: any = {};
    queryArray?.forEach((item) => {
        if (item.split(":")[0] === "name" || item.split(":")[0] === "parent_name") {
            object[item.split(":")[0]] = item.split(":")[1];
        } else {
            object[item.split(":")[0]] = typeChecker(item.split(":")[1]);
        }
    });
    return object;
}

export const searchUrlToString = (object: any) => {
    let qs = "?q=";
    Object.entries(object).forEach((item) => {
        if (item[1] !== undefined) qs += `${item[0]}:${item[1]}|`;
    });
    return qs;
};

export const isValidUrl = (urlString: string) => {
    try {
        return Boolean(new URL(urlString));
    } catch (e) {
        return false;
    }
};
export const addZeroBefore = (n: number) => {
    return (n < 10 ? "0" : "") + n;
};
export const toMinute = (value: TimeType) => addZeroBefore(parseInt(value.h) * 60 + parseInt(value.m));

export function getPathHash() {
    const resource = store.getState().path.currentResource;
    if (resource) return resource.obj_hash;
}

export function getWebEnvConfig(key: string) {
    const webConfigs = store.getState().environment.webListConfig;
    const config = webConfigs.find((item) => item.key === key);
    return config ? config.value : "";
}

export function getAnonListConfig(key: string) {
    const webConfigs = store.getState().environment.anonListConfig;
    const config = webConfigs.find((item) => item.key === key);
    return config ? config.value : "";
}

export function getLiveEnvConfig(key: string) {
    const liveConfigs = store.getState().environment.liveListConfig;
    const config = liveConfigs.find((item) => item.key === key);
    return config ? config.value : null;
}

export function getEnvConfig(key: string) {
    const envConfig = store.getState().environment.listConfig;
    const config = envConfig.find((item) => item.key === key);
    return config ? config.value : null;
}

export function passwordValidator(policies: { pattern: string; message: string }[], password: string) {
    for (let i = 0; i < policies.length; i++) {
        if (!RegExp(policies[i].pattern).test(password)) {
            return { validation: false, message: policies[i].message };
        }
    }
    return { validation: true, message: "" };
}

export const paginate = (array: any[], page_size: number, page_number: number) => array.slice((page_number - 1) * page_size, page_number * page_size);

export const iOSDetection = () => {
    return (
        ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
};

export function base64toFile(base64String: string, filename: string) {
    const arr = base64String.split(",");
    // @ts-ignore
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

export function fileToBase64(file: File) {
    return new Promise((resolve) => {
        // const reader = new FileReader();
        // reader.readAsDataURL(file);
        // reader.onload = function () {
        //     resolve(reader.result);
        // };
        var reader = new FileReader();
        var fileByteArray = [];
        reader.readAsArrayBuffer(file);
        reader.onloadend = function (evt: any) {
            if (evt.target.readyState == FileReader.DONE) {
                var arrayBuffer = evt.target.result,
                    array = new Uint8Array(arrayBuffer);
                resolve(arrayBuffer);
                for (var i = 0; i < array.length; i++) {
                    fileByteArray.push(array[i]);
                }
            }
        };
    });
}

export function getImageUrlBase64(src: any, outputFormat: any) {
    return new Promise((resolve) => {
        let image = new Image();
        image.crossOrigin = "Anonymous";
        image.onload = function () {
            let canvas = document.createElement("canvas");
            let ctx = canvas.getContext("2d");
            let dataURL;
            canvas.height = image.naturalHeight;
            canvas.width = image.naturalWidth;
            ctx!.drawImage(image, 0, 0);
            dataURL = canvas.toDataURL(outputFormat);
            resolve(dataURL);
        };
        image.onerror = function () {
            resolve("");
        };
        image.src = src;
        if (image.complete || image.complete === undefined) {
            image.src = "data:image/gif;base64, R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
            image.src = src;
        }
    });
}

export function fixPhoneNumberFormat(phone: string) {
    if (phone.startsWith("+98")) {
        return phone.replace("+98", "0");
    } else if (phone.startsWith("09")) {
        return phone;
    } else {
        toast({ type: "error", title: `فرمت شماره ${phone} صحیح نمی‌باشد.` });
        return false;
    }
}

export function isNumberInt(value: number) {
    return value % 1 === 0;
}

export const checkParentTarget = (event: any, target: string) => {
    try {
        return [
            event.target.id,
            event.target.parentNode.id,
            event.target.parentNode.parentNode.id,
            event.target.parentNode.parentNode.parentNode.id
        ].includes(target);
    } catch (error) {
        return false;
    }
};

export const getPlanStatus = (purchasePlan: any) => {
    if (!!store.getState().fileManager?.activePurchases) {
        let status: number = 0;
        const serverTime = store.getState().fileManager.activePurchases.serverTime;
        const activeDuration: number = purchasePlan.created_at + purchasePlan.plan.duration * 86400;
        if (serverTime < activeDuration) status = CurrentPlanStatus.active;
        else status = CurrentPlanStatus.out;
        return status;
    } else return 1;
};

export const dateOptions = [
    {
        label: CalendarOptions.ALL_DAYS,
        payload: undefined
    },
    {
        label: CalendarOptions.TODAY,
        payload: [moment().startOf("day").unix(), moment().unix()]
    },
    {
        label: CalendarOptions.YESTERDAY,
        payload: [moment().startOf("day").subtract("1", "day").unix(), moment().endOf("day").subtract("1", "day").unix()]
    },
    {
        label: CalendarOptions.WEEK,
        payload: [
            moment()
                .clone()
                .subtract(7 * 24, "hours")
                .unix(),
            moment(moment()).unix()
        ]
    },
    {
        label: CalendarOptions.MONTH,
        payload: [
            moment()
                .clone()
                .subtract(30 * 24, "hours")
                .unix(),
            moment(moment()).unix()
        ]
    },
    {
        label: CalendarOptions.CUSTOM,
        payload: undefined
    }
];

export const dateRangeCheck = (option: CalendarOptions, start?: number, end?: number) => {
    if (option === CalendarOptions.CUSTOM && start && end) {
        return [start, end];
    } else {
        return dateOptions.find((date) => date.label === option)?.payload;
    }
};

export const removeZeroFromStart = (phoneNumber: string) => {
    return phoneNumber.replace(/^0/, "");
};

export const addZeroToStart = (phoneNumber: string) => {
    return "0" + phoneNumber;
};

export const toNumberArray = (value: string | number | undefined) => {
    if (!value) return [];
    else
        return String(value)
            .split(",")
            .filter((item) => item && !isNaN(+item))
            .map(Number);
};

export function getRelativeTime(timestamp: number) {
    const serverTime = store.getState().environment.serverTime;
    const now = serverTime?.datetime_now || new Date().getTime() / 1000;

    const diff = timestamp - now;

    const seconds = Math.ceil(diff);
    const minutes = Math.ceil(diff / 60);
    const hours = Math.ceil(diff / (60 * 60));
    const days = Math.ceil(diff / (60 * 60 * 24));

    if (Math.abs(seconds) < 60) {
        return { number: seconds, unit: "ثانیه" };
    } else if (Math.abs(minutes) < 60) {
        return { number: minutes, unit: "دقیقه" };
    } else if (Math.abs(hours) < 24) {
        return { number: hours, unit: "ساعت" };
    } else {
        return { number: days, unit: "روز" };
    }
}
